import { Gefahr, Produktlinie, UebergreifendItem, Vertragsoptionen, ZusatzoptionItem } from '@ps20-frontend/frontend-models';
import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import { isNeugeschaeft } from '@ps20-frontend/shop-integration-redux';
import { ExtremwetterUWProcessQuery } from '@ps20-frontend/selector';
import { isOutbuildingRequired, OutbuildingEvalContext } from '@ps20-frontend/uw';
import { TaasTarifrisikoschluesselVWGV2 } from '@ps20/bff-models/dist/lib/ps20-bff-enums';
import { IAppState } from '../../../store/appState';
import { ZusatzoptionItemVWG } from '../../../store/models/zusatzoptionenVWG.model';
import { selectGisFlutzone } from './gisDaten.selector';
import { selectPlzRequiresUw } from './plzRequiresUw.selector';

export const VERSICHERTE_NEBENGEBAEUDE_LEISTUNG_ID = 'versicherteNebengebaeude';
export const HAUS_TECHNIK_SCHUTZ_LEISTUNG_ID = 'hausTechnikSchutz';

export const selectSelectedProduktlinie = (state: IAppState): Produktlinie =>
  getSelectedProduktlinie(state.produkt);

export function mapStateToPremiumAndExtremwetterDisabledSelectorTo(
  state: IAppState
): ExtremwetterUWProcessQuery {
  return {
    elementarSelbstbehaltId: state.elementarSelbstbehalt?.id,
    activateElementar: state.vorbelegungen?.activateElementar,
    flutzone: selectGisFlutzone(state),
    featureToggles: state.transientState?.featureToggles,
    vorschaedenVorhanden: state.vorschaeden?.vorhanden,
    vertragsoptionen: state.vertragsoptionen,
    elementarVorschadenCountFromRisikoortService:
    state.vorschaeden?.elementarVorschaden.countFromRisikoortService,
    risikoortZuerszone: state.risikoort?.zuerszone,
    elementarVorschaedenCount: state.vorschaeden?.elementarVorschaden.count,
    plzRequiresUw: selectPlzRequiresUw(state)
  };
}

export const selectZusatzoptionItems = (state: IAppState): Array<ZusatzoptionItemVWG> => {
  return selectSelectedProductZusatzoptionItems(state);
};

const selectSelectedProductZusatzoptionItems = (state: IAppState): ZusatzoptionItem[] =>
  getSelectedProduktlinie(state.produkt)?.zusatzoptionen || [];

export type SelectZusatzoptionItemsReturnType = ReturnType<typeof selectZusatzoptionItems>;

export const selectProduktlinien = (state: IAppState): Array<Produktlinie> =>
  state.produkt?.produktlinien || [];

export const selectHausTechnikSchutzDeselected = (state: IAppState): boolean => {
  return state.produkt?.produktlinien[2]?.leistungen.find(leistung => leistung.id === HAUS_TECHNIK_SCHUTZ_LEISTUNG_ID)?.abgewaehlt;
};

export const selectVersicherteNebengebaeudeDeselected = (state: IAppState): boolean => {
  const selectedProduktlinie = selectSelectedProduktlinie(state);
  if (!selectedProduktlinie?.leistungen) {
    return false;
  }
  return (selectedProduktlinie.leistungen.find(leistung => leistung.id === VERSICHERTE_NEBENGEBAEUDE_LEISTUNG_ID)?.abgewaehlt ?? false);
};

export const mapStateToOutbuildingEvalContext = (
  state: IAppState,
  vertragsoptionen: Vertragsoptionen,
): OutbuildingEvalContext => ({
  featureToggles: {},
  selectedZusatzoptionen: [],
  isNeugeschaeft: isNeugeschaeft(vertragsoptionen),
  vorvertrag: {
    coveredOutbuildingsOver200:
      vertragsoptionen.vorvertragInformationen?.isNebengebaeudeUeber200qm === true,
  },
  outbuildingTotalAreaSqm: state.gebaeude?.nebengebaeude?.totalArea,
  isInsuredOutbuildingsLeistungDeselected: selectVersicherteNebengebaeudeDeselected(state),
});

export const isUwProzessForNebengebaeudeNecessary = (
  state: IAppState,
  vertragsoptionen: Vertragsoptionen,
): boolean =>
  isOutbuildingRequired(undefined, mapStateToOutbuildingEvalContext(state, vertragsoptionen));

export const selectIsUwProzessForNebengebaeudeNecessary = (state: IAppState): boolean =>
  isUwProzessForNebengebaeudeNecessary(state, state.vertragsoptionen);

export const selectUebergreifendeLeistungen = (state: IAppState): Array<UebergreifendItem> =>
  state.produkt?.uebergreifend || [];

export const selectSelectedZusatzoptionItems = (state: IAppState): Array<ZusatzoptionItem> => {
  return selectSelectedProductZusatzoptionItems(state).filter(elem => elem.isSelected);
};

export const selectFormattedProduktlinienGefahren = (state: IAppState): Array<string> => {
  return selectProduktlinien(state)
    .filter(tarifItem => tarifItem.gefahren?.length > 0)
    .map((tarifItem: Produktlinie) => tarifItem.gefahren)
    .map(getGefahrenAsString)
    .map(getGefahrenCommaSeperated);
};

export const isGefahrSelected = (
  state: IAppState,
  taasTarifrisikoschluesselVWGV2: TaasTarifrisikoschluesselVWGV2
): boolean => {
  return selectSelectedProduktlinie(state)
    .gefahren.filter((gefahr: Gefahr) => gefahr.isSelected)
    .some(g => g.name === taasTarifrisikoschluesselVWGV2);
};

export const isZusatzoptionSelected = (
  state: IAppState,
  taasTarifrisikoschluesselVWGV2: TaasTarifrisikoschluesselVWGV2
): boolean => {
  return selectSelectedProductZusatzoptionItems(state)
    .filter((zusatzoption: ZusatzoptionItem) => zusatzoption.isSelected)
    .some(z => z.name === taasTarifrisikoschluesselVWGV2);
};

export const selectGefahrenByProduktlinie = (state: IAppState): Array<Gefahr> => {
  return getSelectedProduktlinie(state.produkt)?.gefahren || [];
};

function getGefahrenAsString(gefahrArray: Array<Gefahr>): string[] {
  return gefahrArray
    .filter((gefahr: Gefahr) => gefahr.isSelected)
    .map((gefahr: Gefahr) => gefahr.label);
}

function getGefahrenCommaSeperated(gefahrArrayAsStrings: Array<string>): string {
  return gefahrArrayAsStrings.toString().replace(/,/gi, ', ');
}

export const selectSelectedProduktlinieIndex = (state: IAppState): number => {
  return selectProduktlinien(state).findIndex(produktlinie => produktlinie.selected);
};
